import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input, FormGroup, Label, FormFeedback } from "reactstrap";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import axios from "axios";
import { baseUrl, uploadFile } from "../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from "react-router-dom";
import { FaSave, FaEye, FaEyeSlash } from "react-icons/fa";

interface RoleOption {
  id: number;
  name: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  roleId?: string;
  designation?: string;
  phone?: string;
  address?: string;
  joiningDate?: string;
  imageFile?: string;
}

export default function AddEmployee() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [roleId, setRoleId] = useState("");
  const [designation, setDesignation] = useState("");
  const [phone, setPhone] = useState("971");
  const [address, setAddress] = useState("");
  const [joiningDate, setJoiningDate] = useState<Date | null>(new Date());
  const [status, setStatus] = useState("Active");
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const fetchRoleList = async () => {
      setLoadingRoles(true);
      try {
        const response = await axios.get(`${baseUrl}role/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoles(response.data || []);
      } catch (error) {
        console.error("Error fetching roles list:", error);
        toast.error("Failed to load roles list.");
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoleList();
  }, [token]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

      if (!validTypes.includes(file.type.toLowerCase())) {
        setErrors((prev) => ({
          ...prev,
          imageFile: "Only JPG, JPEG, PNG, or WEBP images are allowed.",
        }));
        setImageFile(null);
        e.target.value = "";
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          imageFile: "Profile image size must be less than 2MB.",
        }));
        setImageFile(null);
        e.target.value = "";
        return;
      }

      setErrors((prev) => ({ ...prev, imageFile: undefined }));
      setImageFile(file);
    }
  };

  const uploadProfileImage = async (file: File): Promise<string> => {
    return await uploadFile(file);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = "Full name is required.";
      isValid = false;
    }

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(email.trim())) {
      newErrors.email = "Invalid email address";
      isValid = false;
    }

    if (!password) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      isValid = false;
    }

    if (!roleId) {
      newErrors.roleId = "Role is required.";
      isValid = false;
    }

    if (!designation.trim()) {
      newErrors.designation = "Designation is required.";
      isValid = false;
    }

    // Phone validation
    const digitsOnly = phone.replace(/\D/g, "");
    if (!digitsOnly || digitsOnly.length < 7) {
      newErrors.phone = "Valid phone number is required.";
      isValid = false;
    }

    if (!joiningDate) {
      newErrors.joiningDate = "Joining date is required.";
      isValid = false;
    }

    if (!address.trim()) {
      newErrors.address = "Address is required.";
      isValid = false;
    }

    if (!imageFile) {
      newErrors.imageFile = "Profile image is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      let uploadedFilename = "default_user.png";
      if (imageFile) {
        uploadedFilename = await uploadProfileImage(imageFile);
      }

      const formattedJoiningDate = joiningDate ? joiningDate.toISOString().split("T")[0] : "";
      const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;

      await axios.post(
        `${baseUrl}employee/store`,
        {
          name: name.trim(),
          email: email.trim(),
          password,
          role_id: Number(roleId),
          designation: designation.trim(),
          phone: formattedPhone,
          address: address.trim(),
          joining_date: formattedJoiningDate,
          status,
          profile_image: uploadedFilename,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Employee created successfully!");
      setTimeout(() => {
        navigate("/employee");
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to create employee.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-body">
      <Breadcrumbs pageTitle="Add Employee" parent="Employee Management" title="Add Employee" />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="p-4">
            <form onSubmit={handleSubmit} noValidate autoComplete="none">
              {/* Dummy hidden inputs to absorb browser autofill */}
              <input type="text" name="prevent_autofill_user" style={{ display: "none" }} tabIndex={-1} />
              <input type="password" name="prevent_autofill_pwd" style={{ display: "none" }} tabIndex={-1} />

              <Row>
                <Col xl={6} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Full Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="emp_full_name_input"
                      id="emp_full_name_input"
                      placeholder="Enter full name"
                      value={name}
                      autoComplete="one-time-code"
                      onChange={(e) => {
                        setName(e.target.value);
                        setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      invalid={!!errors.name}
                    />
                    <FormFeedback>{errors.name}</FormFeedback>
                  </FormGroup>
                </Col>

                <Col xl={6} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Email Address <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="emp_email_address_input"
                      id="emp_email_address_input"
                      placeholder="Enter email address"
                      value={email}
                      autoComplete="one-time-code"
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      invalid={!!errors.email}
                    />
                    <FormFeedback>{errors.email}</FormFeedback>
                  </FormGroup>
                </Col>



                <Col xl={4} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Assign Role <span className="text-danger">*</span>
                    </Label>
                    {loadingRoles ? (
                      <div>
                        <Spinner size="sm" color="primary" />
                      </div>
                    ) : (
                      <Input
                        type="select"
                        name="emp_role_id_select"
                        id="emp_role_id_select"
                        value={roleId}
                        onChange={(e) => {
                          setRoleId(e.target.value);
                          setErrors((prev) => ({ ...prev, roleId: undefined }));
                        }}
                        invalid={!!errors.roleId}
                      >
                        <option value="">Select Role</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </Input>
                    )}
                    <FormFeedback>{errors.roleId}</FormFeedback>
                  </FormGroup>
                </Col>

                <Col xl={4} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Designation <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="emp_designation_input"
                      id="emp_designation_input"
                      placeholder="e.g. Senior Support Executive"
                      value={designation}
                      autoComplete="one-time-code"
                      onChange={(e) => {
                        setDesignation(e.target.value);
                        setErrors((prev) => ({ ...prev, designation: undefined }));
                      }}
                      invalid={!!errors.designation}
                    />
                    <FormFeedback>{errors.designation}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col xl={4} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Status <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Input>
                  </FormGroup>
                </Col>

                <Col xl={6} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Phone Number <span className="text-danger">*</span>
                    </Label>
                    <PhoneInput
                      country={"ae"}
                      onlyCountries={["ae", "sa", "in"]}
                      value={phone}
                      onChange={(val) => {
                        setPhone(val);
                        setErrors((prev) => ({ ...prev, phone: undefined }));
                      }}
                      inputStyle={{
                        width: "100%",
                        height: "38px",
                        borderColor: errors.phone ? "#dc3545" : "#ced4da",
                      }}
                      enableSearch
                      placeholder="Enter phone number"
                    />
                    {errors.phone && <div className="text-danger small mt-1">{errors.phone}</div>}
                  </FormGroup>
                </Col>

                <Col xl={6} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Joining Date <span className="text-danger">*</span>
                    </Label>
                    <div>
                      <DatePicker
                        selected={joiningDate}
                        onChange={(d) => {
                          setJoiningDate(d);
                          setErrors((prev) => ({ ...prev, joiningDate: undefined }));
                        }}
                        dateFormat="yyyy-MM-dd"
                        className={`form-control ${errors.joiningDate ? "is-invalid" : ""}`}
                        placeholderText="Select joining date"
                        maxDate={new Date()}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                    {errors.joiningDate && <div className="text-danger small mt-1">{errors.joiningDate}</div>}
                  </FormGroup>
                </Col>



                <Col xl={12} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Address <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      rows={3}
                      name="emp_address_input"
                      id="emp_address_input"
                      placeholder="Enter employee full address"
                      value={address}
                      autoComplete="one-time-code"
                      onChange={(e) => {
                        setAddress(e.target.value);
                        setErrors((prev) => ({ ...prev, address: undefined }));
                      }}
                      invalid={!!errors.address}
                    />
                    <FormFeedback>{errors.address}</FormFeedback>
                  </FormGroup>
                </Col>

                <Col xl={6} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Profile Image <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg, image/webp"
                      onChange={handleImageChange}
                      invalid={!!errors.imageFile}
                    />
                    <FormFeedback>{errors.imageFile}</FormFeedback>
                  </FormGroup>
                </Col>

                <Col xl={6} className="mb-3">
                  <FormGroup>
                    <Label className="font-weight-bold">
                      Password <span className="text-danger">*</span>
                    </Label>
                    <div className="position-relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        name="emp_password_input"
                        id="emp_password_input"
                        placeholder="Enter password (min 6 characters)"
                        value={password}
                        autoComplete="new-password"
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setErrors((prev) => ({ ...prev, password: undefined }));
                        }}
                        invalid={!!errors.password}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#6c757d",
                        }}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                    {errors.password && <div className="text-danger small mt-1">{errors.password}</div>}
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex gap-2 justify-content-start mt-2">
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" /> Save Employee
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </div>
  );
}
